import React, { useEffect, useState, useRef } from "react";
import { loadStyles } from "../../../utils/cssLoader";
import { getData } from "../../../utils/getData";
import { ColorVariables } from "../../../utils/general";
import { getImageUrl } from "../../../utils/generalLiveCenter";
import { useSocket } from "../../../hooks/useSocket";

/**
 * EditorialHeader — a self-contained, live football match-header widget.
 *
 * Renders the head + state + events for a match, adapting to match state:
 *   • Upcoming  → kickoff label + "VS" + a live countdown
 *   • Live      → pulsing live minute + current score + goals/cards so far (socket-updated)
 *   • Finished  → final score (FT / AET / Pens) + full goals/cards list
 *
 * Data source (priority):
 *   1. explicit view props (home/away/…) → static render (demo/preview)
 *   2. dataId + baseUrl → fetches /match/{id}/detail (head) AND /match/{id}/summary (events)
 *
 * Styling is fully token-driven (see src/assets/scss/editorialHeader.scss); when baseUrl
 * is present it also fetches /settings and injects <style id="dynamic-colors">.
 */

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const pad2 = (n) => String(n).padStart(2, "0");

// The feed's match.dt is a UTC wall-clock string "YYYY-MM-DD HH:mm:ss" with NO zone suffix
// (confirmed against the live API — the same dt comes back regardless of the difference/country
// headers). Append "Z" so it is parsed as UTC, never as the browser's local zone, giving a
// correct absolute instant on any device.
const kickoffMs = (dt) => {
  if (!dt) return NaN;
  let s = String(dt).trim().replace(" ", "T");
  if (!/[zZ]$|[+\-]\d{2}:?\d{2}$/.test(s)) s += "Z";
  return new Date(s).getTime();
};

// Kickoff wall-clock parts. match.dt is UTC. If an explicit `difference` (viewer offset, e.g.
// "+5") is provided we shift by it and read UTC parts (deterministic + SSR-stable). If it is
// 0/absent we fall back to the VIEWER'S OWN device timezone via local getters — so the time is
// right for whoever is looking, without the offset having to be wired up.
const kickoffParts = (dt, difference) => {
  const base = kickoffMs(dt);
  if (isNaN(base)) return null;
  const offH = parseInt(String(difference ?? "").replace("+", ""), 10) || 0;
  if (offH !== 0) {
    const d = new Date(base + offH * 3600000);
    return {
      date: d.getUTCDate(),
      month: d.getUTCMonth(),
      year: d.getUTCFullYear(),
      h: d.getUTCHours(),
      m: d.getUTCMinutes(),
    };
  }
  const d = new Date(base); // viewer's device timezone
  return {
    date: d.getDate(),
    month: d.getMonth(),
    year: d.getFullYear(),
    h: d.getHours(),
    m: d.getMinutes(),
  };
};

const formatMatchDate = (dt, difference) => {
  const p = kickoffParts(dt, difference);
  if (!p) return "";
  return `${p.date} ${MONTHS[p.month]} ${p.year}`;
};

const fmtAttendance = (a) => {
  if (a == null || a === "") return "";
  const n = typeof a === "number" ? a : parseInt(String(a).replace(/[^0-9]/g, ""), 10);
  return isNaN(n) ? String(a) : n.toLocaleString("en-US");
};

const splitScore = (s) =>
  typeof s === "string" && s.includes("-") ? s.split("-").map((x) => x.trim()) : [null, null];

// Local kickoff clock label for upcoming matches, e.g. "7 Jul · 00:00". A 19:00 UTC kickoff
// reads as "00:00" next day for a UTC+5 viewer (via explicit "+5" or their device timezone).
const kickoffLabel = (dt, difference) => {
  const p = kickoffParts(dt, difference);
  if (!p) return "Upcoming";
  return `${p.date} ${MONTHS[p.month]} · ${pad2(p.h)}:${pad2(p.m)}`;
};

// ── Event vocabulary (goals + cards only; subs / HT / FT / disallowed excluded) ──
const GOAL_TYPES = ["Goal", "Own goal", "Penalty"];
const CARD_KIND = { "Yellow card": "y", "Second yellow card": "y", "Red card": "r" };
const eventKind = (typ) =>
  GOAL_TYPES.includes(typ) ? "goal" : CARD_KIND[typ] || null;

// Raw summary.timeline events → normalized, filtered, minute-sorted list.
const normalizeEvents = (rawEvents) =>
  (rawEvents || [])
    .map((e) => ({ kind: eventKind(e?.typ), e }))
    .filter(({ kind, e }) => kind && (e?.pl_nm || e?.pl_in_nm))
    .map(({ kind, e }) => ({
      kind,
      typ: e.typ,
      mins: e.mins,
      isHome: e.isH === 1 || e.isH === true,
      player: e.pl_nm || e.pl_in_nm,
    }))
    .sort((a, b) => (parseInt(a.mins, 10) || 0) - (parseInt(b.mins, 10) || 0));

// ── Football match-state classification ─────────────────────────────────────
// The feed mixes case ("inprogress" lowercase, "Finished"/"Full Time" capitalized), so ALWAYS
// compare lowercased. `st` is the primary status text; `statusKey` is the numeric status
// (1 = not started, 3 = result only, 4 = finished, 15 = live); the live minute is in `mins`.
const FB_LIVE = [
  "inprogress",
  "1st half",
  "2nd half",
  "half time",
  "et 1st half",
  "et 2nd half",
  "penalty",
];
// Normal full-time finishes (→ "FT" / "FT · AET" / "FT · Pens").
const FB_FT = ["finished", "finished aet", "finished ap", "full time"];
// Ended but NOT a normal finish — still "closed" (show final score/events), but each keeps its
// own word instead of a misleading "FT".
const FB_CLOSED = {
  awarded: "Awarded",
  "result only": "Result",
  cancelled: "Cancelled",
  postponed: "Postponed",
  abandoned: "Abandoned",
  interrupted: "Interrupted",
  deleted: "Cancelled",
};

const stLower = (match) => String(match?.st || "").trim().toLowerCase();
const isLiveStatus = (match) =>
  parseInt(match?.statusKey, 10) === 15 || FB_LIVE.includes(stLower(match));

// Raw /match/{id}/detail `.data` object + events → the view model this widget renders.
const deriveView = (match, events, sport, difference, liveMinute) => {
  if (!match) return null;
  const c = match.competitors || {};
  const st = String(match.st || "");
  const stLow = st.trim().toLowerCase();
  const sKey = parseInt(match.statusKey, 10);

  const isLive = sKey === 15 || FB_LIVE.includes(stLow);
  const isFT = sKey === 4 || FB_FT.includes(stLow); // normal full-time
  // Awarded / cancelled / postponed / … — ended but not a normal FT (statusKey 3 = result only).
  const closedLabel = FB_CLOSED[stLow] || (sKey === 3 ? "Result" : null);
  const isClosed = !isLive && !!closedLabel;
  const isFinished = !isLive && (isFT || isClosed);
  // Fallback rule: anything NOT recognized as live/finished/closed is UPCOMING — a new/unknown
  // status (e.g. "Suspended") must never render as FT by accident.
  const isUpcoming = !isLive && !isFinished;
  const state = isLive ? "live" : isFinished ? "finished" : "upcoming";

  const [hs, as] = splitScore(match.cfs || match.fs || "");
  const isAet = /aet/i.test(stLow) || match.mins === "FAET";
  const isPens = /\bap\b/i.test(stLow) || !!match.ps;

  // Winner (finished matches only): compare the DECISIVE score — penalties (ps) when the match
  // was decided on pens, else the full/cfs score. Higher side wins; equal = draw (no winner).
  let winnerSide = null;
  if (isFinished) {
    const decisive = match.mins === "FAP" || match.ps ? match.ps : match.cfs || match.fs;
    const [dh, da] = splitScore(decisive || "");
    const nh = parseInt(dh, 10);
    const na = parseInt(da, 10);
    if (!isNaN(nh) && !isNaN(na)) winnerSide = nh > na ? "home" : na > nh ? "away" : null;
  }

  // Status chip shows the plain state word (LIVE / UPCOMING / FINISHED — uppercased by CSS).
  // The blinking dot signals "live"; abnormal-ended matches keep their own word.
  let statusLabel;
  if (isLive) statusLabel = "Live";
  else if (isUpcoming) statusLabel = "Upcoming";
  else if (isClosed) statusLabel = closedLabel; // Cancelled / Postponed / Awarded / Result / …
  else {
    statusLabel = "Finished";
    if (isAet) statusLabel += " · AET";
    if (isPens) statusLabel += " · Pens";
  }

  const timeline = [];
  if (match.ht) timeline.push({ label: "HT", score: match.ht });
  if (match.cfs) timeline.push({ label: "FT", score: match.cfs, active: !isAet });
  if (isAet) timeline.push({ label: "AET", score: match.cfs, active: true });

  return {
    state,
    isLive,
    isUpcoming,
    isFinished,
    winnerSide,
    pens: isFinished && isPens && match.ps ? match.ps : null, // "4-3" penalty shootout score
    isAet,
    competition: match.tournament?.nm || match.league?.nm || "",
    stage: match.ro || "",
    statusLabel,
    home: { name: c.htn, flag: c.htid ? getImageUrl("", "", sport, c.htid) : null, score: hs ?? "-", form: c.htf },
    away: { name: c.atn, flag: c.atid ? getImageUrl("", "", sport, c.atid) : null, score: as ?? "-", form: c.atf },
    timeline,
    events: normalizeEvents(events),
    meta: {
      date: formatMatchDate(match.dt, difference),
      venue: match.venue?.name || match.std || "",
      referee: match.match_official?.official?.name || "",
      attendance: fmtAttendance(match.attendance ?? match.spectators),
    },
    kickoff: match.dt,
  };
};

// ── Small pieces ────────────────────────────────────────────────────────────
const FormDots = ({ form = [] }) => {
  if (!form?.length) return null;
  return (
    <div className="eh__form">
      {form.slice(0, 5).reverse().map((r, i) => {
        const k = String(r || "").toUpperCase();
        const mod = k === "W" ? "w" : k === "L" ? "l" : "d";
        return (
          <span key={i} className={`eh__dot eh__dot--${mod}`} title={k}>
            {k || mod.toUpperCase()}
          </span>
        );
      })}
    </div>
  );
};

const Flag = ({ flag, name }) => (
  <span className="eh__flag">
    {typeof flag === "string" ? <img src={flag} alt={name ? `${name} logo` : ""} /> : flag || null}
  </span>
);

const EventIcon = ({ kind }) =>
  kind === "goal" ? (
    <span className="eh__event-ico eh__event-ico--goal" aria-hidden="true">⚽</span>
  ) : (
    <span className={`eh__event-ico eh__event-ico--card eh__card--${kind}`} aria-hidden="true" />
  );

const EventRow = ({ ev }) => (
  <div className={`eh__event eh__event--${ev.isHome ? "home" : "away"}`}>
    {ev.isHome ? (
      <>
        <EventIcon kind={ev.kind} />
        <span className="eh__event-min">{ev.mins}'</span>
        <span className="eh__event-player">{ev.player}</span>
      </>
    ) : (
      <>
        <span className="eh__event-player">{ev.player}</span>
        <span className="eh__event-min">{ev.mins}'</span>
        <EventIcon kind={ev.kind} />
      </>
    )}
  </div>
);

// Live countdown (Days / Hrs / Min / Sec). `targetMs` is the ABSOLUTE kickoff epoch (UTC);
// subtracting Date.now() (also absolute) gives the true remaining time on any device, with zero
// timezone math. `whenText` is the viewer-local kickoff label shown underneath for context.
const Countdown = ({ targetMs, whenText }) => {
  const calc = () => {
    const diff = targetMs - Date.now();
    if (isNaN(diff) || diff <= 0) return { d: 0, h: 0, m: 0, s: 0 };
    return {
      d: Math.floor(diff / 86400000),
      h: Math.floor((diff / 3600000) % 24),
      m: Math.floor((diff / 60000) % 60),
      s: Math.floor((diff / 1000) % 60),
    };
  };
  const [t, setT] = useState(calc);
  useEffect(() => {
    const id = setInterval(() => setT(calc()), 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetMs]);
  const cell = (n, l) => (
    <div className="eh__cd-cell">
      <span className="eh__cd-num">{String(n).padStart(2, "0")}</span>
      <span className="eh__cd-lbl">{l}</span>
    </div>
  );
  return (
    <div className="eh__countdown">
      <span className="eh__cd-title">Kicks off in</span>
      <div className="eh__cd-cells">
        {cell(t.d, "Days")}
        <span className="eh__cd-sep">:</span>
        {cell(t.h, "Hrs")}
        <span className="eh__cd-sep">:</span>
        {cell(t.m, "Min")}
        <span className="eh__cd-sep">:</span>
        {cell(t.s, "Sec")}
      </div>
      {whenText ? <span className="eh__cd-when">{whenText}</span> : null}
    </div>
  );
};

const Svg = ({ children }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);
const ICONS = {
  date: (<Svg><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></Svg>),
  venue: (<Svg><path d="M12 21s-7-6.4-7-11a7 7 0 0 1 14 0c0 4.6-7 11-7 11z" /><circle cx="12" cy="10" r="2.5" /></Svg>),
  referee: (<Svg><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></Svg>),
  attendance: (<Svg><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></Svg>),
};

// ── Main component ────────────────────────────────────────────────────────────
const EditorialHeader = ({
  dataId,
  baseUrl,
  sport = "football",
  difference = "+0",
  socketUrl,
  match: matchProp = null,
  dataClient = false,
  // explicit / demo props:
  state: stateProp,
  competition,
  stage,
  status,
  home,
  away,
  timeline,
  events: eventsProp,
  meta,
  kickoff,
  loadCSS = true,
}) => {
  const explicit = !!(home && home.name);

  useEffect(() => {
    if (loadCSS === true || loadCSS === "true") loadStyles("editorialHeader");
  }, [loadCSS]);

  // Dynamic CSS variables — fetch /settings → inject <style id="dynamic-colors">
  useEffect(() => {
    if (!baseUrl) return;
    let cancelled = false;
    fetch(`${baseUrl}/settings`, {
      headers: {
        Token: process.env.REACT_APP_SETTINGS_TOKEN,
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.statusText)))
      .then((settings) => {
        if (cancelled) return;
        const css = ColorVariables(settings);
        let tag = document.getElementById("dynamic-colors");
        if (!tag) {
          tag = document.createElement("style");
          tag.id = "dynamic-colors";
          document.head.appendChild(tag);
        }
        tag.textContent = css;
      })
      .catch((err) => console.warn("EditorialHeader: settings load failed —", err));
    return () => {
      cancelled = true;
    };
  }, [baseUrl]);

  // Data sources (priority): SSR props → hydration DOM → embed self-fetch
  const [match, setMatch] = useState(matchProp || null);
  const [events, setEvents] = useState(eventsProp || []);

  // SSR: sync incoming props into state
  useEffect(() => {
    if (matchProp) setMatch(matchProp);
  }, [matchProp]);
  useEffect(() => {
    if (eventsProp) setEvents(eventsProp);
  }, [eventsProp]);

  // Hydration: re-read the serialized { headData, data } off the container
  useEffect(() => {
    if (!dataClient) return;
    try {
      const raw = document.querySelector(".microservice-cont")?.getAttribute("data-listingdata");
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed?.headData?.data) setMatch(parsed.headData.data);
      setEvents(parsed?.data?.summary?.timeline || []);
    } catch (e) {
      console.error("[EditorialHeader] data-listingdata parse error:", e);
    }
  }, [dataClient]);

  // Embed: self-fetch head (detail) + events (summary) together
  useEffect(() => {
    if (explicit || dataClient || matchProp || !dataId || !baseUrl) return;
    let cancelled = false;
    Promise.all([
      getData({ dataId, dataType: "match", baseUrl, sport, difference }),
      getData({ dataId, dataType: "match-summary", baseUrl, sport, difference }),
    ]).then(([detail, summary]) => {
      if (cancelled) return;
      setMatch(detail?.data || detail?.match || detail || null);
      setEvents(summary?.summary?.timeline || []);
    });
    return () => {
      cancelled = true;
    };
  }, [explicit, dataClient, matchProp, dataId, baseUrl, sport, difference]);

  // Live minute — seed from match.mins (the live-minute field), +1 every 60s while live
  // (reseeded when the socket pushes a new mins/status).
  const isLiveNow = isLiveStatus(match);
  const [liveMinute, setLiveMinute] = useState(null);
  const timerRef = useRef(null);
  useEffect(() => {
    clearInterval(timerRef.current);
    const m = parseInt(match?.mins ?? match?.cp, 10);
    if (isLiveNow && !isNaN(m)) {
      setLiveMinute(m);
      timerRef.current = setInterval(() => setLiveMinute((p) => (p != null ? p + 1 : p)), 60000);
    } else {
      setLiveMinute(null);
    }
    return () => clearInterval(timerRef.current);
  }, [match?.mins, match?.cp, isLiveNow]);

  // Real-time — subscribe to the match socket; merge score/status, re-fetch events on score change
  const resolvedSocketUrl = explicit
    ? ""
    : socketUrl || process.env.REACT_APP_SOCKET || "https://socket-bolsaas.6lgx.com/";
  const socket = useSocket(resolvedSocketUrl);
  const lastScoreRef = useRef(null);
  useEffect(() => {
    if (explicit || !socket || !dataId) return;
    const channel = "FootballMatch";
    const event = `FootballMatchDetails-${dataId}`;
    const handler = (e) => {
      const incoming = e?.data?.data;
      if (!incoming || String(incoming.id) !== String(dataId)) return;
      setMatch((prev) => ({ ...(prev || {}), ...incoming }));
      if (incoming.cfs && incoming.cfs !== lastScoreRef.current && baseUrl) {
        lastScoreRef.current = incoming.cfs;
        getData({ dataId, dataType: "match-summary", baseUrl, sport, difference }).then((r) =>
          setEvents(r?.summary?.timeline || []),
        );
      }
    };
    socket.channel(channel)?.listen(event, handler);
    return () => {
      try {
        socket.channel(channel)?.stopListening(event, handler);
      } catch (_) {}
    };
  }, [socket, dataId, baseUrl, sport, difference, explicit]);

  // Build the view model
  let view;
  if (explicit) {
    const evList = normalizeEvents(eventsProp);
    const inferred =
      stateProp ||
      (/'$/.test(String(status || "")) ? "live" : /ft|full|aet|finished|pens/i.test(String(status || "")) ? "finished" : "upcoming");
    view = {
      state: inferred,
      isLive: inferred === "live",
      isUpcoming: inferred === "upcoming",
      isFinished: inferred === "finished",
      winnerSide:
        inferred === "finished" && home?.score != null && away?.score != null
          ? Number(home.score) > Number(away.score)
            ? "home"
            : Number(away.score) > Number(home.score)
              ? "away"
              : null
          : null,
      competition,
      stage,
      statusLabel: status,
      home: home || {},
      away: away || {},
      timeline: timeline || [],
      events: evList,
      meta: meta || {},
      kickoff,
    };
  } else {
    view = deriveView(match, events, sport, difference, liveMinute);
  }

  if (!view || (!explicit && !match)) {
    return (
      <div className="eh eh--loading">
        <span>Loading match…</span>
      </div>
    );
  }

  const {
    competition: comp = "",
    stage: stg = "",
    statusLabel = "",
    isLive = false,
    isUpcoming = false,
    home: H = {},
    away: A = {},
    timeline: TL = [],
    events: EV = [],
    meta: M = {},
    state = "finished",
    winnerSide = null,
    pens = null,
    isAet = false,
  } = view;

  const title = [comp, stg].filter(Boolean).join(" · ");
  const hasFooter = M && (M.date || M.venue || M.referee || M.attendance);

  // Countdown target — the ABSOLUTE kickoff instant (match.dt is UTC). Date.now() is also
  // absolute, so the remaining time is correct on every device, no timezone math involved.
  let cdTargetMs = null;
  if (isUpcoming && view.kickoff) {
    const ms = kickoffMs(view.kickoff);
    if (!isNaN(ms)) cdTargetMs = ms;
  }

  // Match-minute timeline layout: place goals + cards on the actual clock.
  // matchEnd = 90 (or 120 when the match ran to extra time). Events that land in
  // the same minute-window share ONE marker + ONE connector line (e.g. a 90'
  // yellow card + a 90' goal show as "90'" once, with the chip and ⚽ together).
  const matchEnd = EV.some((e) => (parseInt(e.mins, 10) || 0) > 90) ? 120 : 90;
  const GAP_MIN = 3; // events within this many minutes are grouped onto one marker/line
  const MIN_GAP = 0.1; // min horizontal spacing between markers (fraction of the line)
  const mmin = (e) => parseInt(e.mins, 10) || 0;
  const placed = [];
  ["home", "away"].forEach((side) => {
    const arr = EV.filter((e) => (e.isHome ? "home" : "away") === side); // minute-sorted
    const clusters = [];
    let i = 0;
    while (i < arr.length) {
      let j = i;
      while (j + 1 < arr.length && mmin(arr[j + 1]) - mmin(arr[j]) <= GAP_MIN) j++;
      const cluster = arr.slice(i, j + 1);
      const mins = mmin(cluster[0]);
      clusters.push({
        side,
        mins,
        frac: Math.max(0, Math.min(1, mins / matchEnd)),
        // full per-event detail — icons on the line + the hover tooltip both read from this
        items: cluster.map((e) => ({ kind: e.kind, typ: e.typ, player: e.player, mins: mmin(e) })),
      });
      i = j + 1;
    }
    // Collision avoidance: a busy match (many cards/goals close together) would otherwise pile the
    // markers on top of each other. Nudge any marker sitting within MIN_GAP of the previous one to
    // the right; order + true minute (shown on the label/tooltip) are kept, only the x is adjusted.
    for (let k = 1; k < clusters.length; k++) {
      if (clusters[k].frac < clusters[k - 1].frac + MIN_GAP) {
        clusters[k].frac = clusters[k - 1].frac + MIN_GAP;
      }
    }
    // if the spread pushed the last marker past the end of the line, shift the whole row back left
    const overshoot = clusters.length ? clusters[clusters.length - 1].frac - 1 : 0;
    if (overshoot > 0) clusters.forEach((c) => (c.frac = Math.max(0, c.frac - overshoot)));
    placed.push(...clusters);
  });

  return (
    <div className={`eh eh--${state}`}>
      {/* Top row */}
      <div className="eh__top">
        <span className="eh__competition">{title}</span>
        {statusLabel ? (
          <span className={`eh__status${isLive ? " eh__status--live" : ""}`}>{statusLabel}</span>
        ) : null}
      </div>

      {/* Teams + score */}
      <div className="eh__main">
        <div className={`eh__team eh__team--home${winnerSide === "home" ? " eh__team--win" : ""}`}>
          <div className="eh__id">
            <span className="eh__nameline">
              <span className="eh__name">{H.name}</span>
              {winnerSide === "home" && <span className="eh__trophy" aria-hidden="true">🏆</span>}
            </span>
            <FormDots form={H.form} />
          </div>
          <Flag flag={H.flag} name={H.name} />
        </div>

        <div className="eh__score">
          {isUpcoming ? (
            <span className="eh__vs">VS</span>
          ) : (
            <>
              <div className="eh__score-line">
                <span className={`eh__score-num${winnerSide === "home" ? " eh__score-num--win" : ""}`}>
                  {H.score ?? "-"}
                </span>
                <span className="eh__score-sep">–</span>
                <span className={`eh__score-num${winnerSide === "away" ? " eh__score-num--win" : ""}`}>
                  {A.score ?? "-"}
                </span>
              </div>
              {(isAet || pens) && <span className="eh__aet">After Extra Time</span>}
              {pens &&
                (() => {
                  const [ph = "", pa = ""] = String(pens).split(/[-:]/);
                  return (
                    <span className="eh__pens">
                      PEN <b>{ph.trim()}</b> - <b>{pa.trim()}</b>
                    </span>
                  );
                })()}
            </>
          )}
        </div>

        <div className={`eh__team eh__team--away${winnerSide === "away" ? " eh__team--win" : ""}`}>
          <Flag flag={A.flag} name={A.name} />
          <div className="eh__id">
            <span className="eh__nameline">
              {winnerSide === "away" && <span className="eh__trophy" aria-hidden="true">🏆</span>}
              <span className="eh__name">{A.name}</span>
            </span>
            <FormDots form={A.form} />
          </div>
        </div>
      </div>

      {/* Upcoming → countdown */}
      {isUpcoming && cdTargetMs != null && (
        <Countdown targetMs={cdTargetMs} whenText={kickoffLabel(view.kickoff, difference)} />
      )}

      {/* Live / Finished → match-minute timeline: goals + cards on the clock */}
      {!isUpcoming && EV.length > 0 && (
        <div className="eh__mtl">
          <div className="eh__mtl-line" />
          <span className="eh__mtl-tick" style={{ left: `calc(10px + (100% - 20px) * ${45 / matchEnd})` }}>
            <span className="eh__mtl-tlabel">HT</span>
          </span>
          <span
            className={`eh__mtl-tick${matchEnd === 90 ? " eh__mtl-tick--active" : ""}`}
            style={{ left: `calc(10px + (100% - 20px) * ${90 / matchEnd})` }}
          >
            <span className="eh__mtl-tlabel">FT</span>
          </span>
          {matchEnd > 90 && (
            <span className="eh__mtl-tick eh__mtl-tick--active" style={{ left: "calc(100% - 10px)" }}>
              <span className="eh__mtl-tlabel">AET</span>
            </span>
          )}
          {placed.map((c, i) => {
            // keep the tooltip inside the card: anchor it left/right for edge markers
            const tip = c.frac < 0.2 ? "s" : c.frac > 0.8 ? "e" : "c";
            // aria-label (not title) → screen-reader accessible without a second native tooltip
            const label = c.items.map((it) => `${it.mins}' ${it.typ} — ${it.player}`).join(", ");
            return (
              <span
                key={`${c.side}-${c.mins}-${i}`}
                className={`eh__mev eh__mev--${c.side} eh__mev--tip-${tip}`}
                style={{ left: `calc(10px + (100% - 20px) * ${c.frac})` }}
                aria-label={label}
              >
                <span className="eh__mev-min">{c.mins}'</span>
                <span className="eh__mev-icons">
                  {c.items.map((it, k) =>
                    it.kind === "goal" ? (
                      <span key={k} className="eh__mev-node eh__mev-node--goal" aria-hidden="true">
                        ⚽
                      </span>
                    ) : (
                      <span
                        key={k}
                        className={`eh__mev-node eh__mev-node--card eh__card--${it.kind}`}
                        aria-hidden="true"
                      />
                    )
                  )}
                </span>
                <span className="eh__mev-tip" role="tooltip">
                  {c.items.map((it, k) => (
                    <span key={k} className="eh__tip-row">
                      {it.kind === "goal" ? (
                        <span className="eh__tip-ico eh__tip-ico--goal" aria-hidden="true">⚽</span>
                      ) : (
                        <span
                          className={`eh__tip-ico eh__tip-chip eh__card--${it.kind}`}
                          aria-hidden="true"
                        />
                      )}
                      <span className="eh__tip-txt">
                        <b>{it.player}</b> · {it.typ} {it.mins}'
                      </span>
                    </span>
                  ))}
                </span>
              </span>
            );
          })}
        </div>
      )}

      {/* Footer meta */}
      {hasFooter && (
        <div className="eh__footer">
          {M.date && (<span className="eh__meta">{ICONS.date}{M.date}</span>)}
          {M.venue && (<span className="eh__meta">{ICONS.venue}{M.venue}</span>)}
          {M.referee && (<span className="eh__meta">{ICONS.referee}{M.referee}</span>)}
          {M.attendance && (<span className="eh__meta">{ICONS.attendance}{M.attendance}</span>)}
        </div>
      )}
    </div>
  );
};

export default EditorialHeader;
